<script setup lang="ts">
import AuthLayout from '../components/AuthLayout.vue';
import { Button } from '../components/ui';

interface ConsentPayload {
  screen: 'authorization';
  title: string;
  promptBeforeClient: string;
  clientName: string;
  promptAfterClient: string;
  warning: string;
  permissionsLabel: string;
  redirectUri: string;
  scopes: string[];
  authorizeLabel: string;
  denyLabel: string;
}

interface ErrorPayload {
  screen: 'error';
  title: string;
  error: string;
}

type AuthorizationPayload = ConsentPayload | ErrorPayload;

const payloadElement = document.getElementById('oauth-authorization-data')!;
const payload = JSON.parse(payloadElement.dataset.payload!) as AuthorizationPayload;
</script>

<template>
  <AuthLayout :title="payload.title" :top-links="[]" :bottom-links="[]">
    <main v-if="payload.screen === 'authorization'" class="min-w-0 leading-6 text-white">
      <p class="m-0 mb-6 text-center text-lg wrap-break-word">
        {{ payload.promptBeforeClient }}<strong>{{ payload.clientName }}</strong
        >{{ payload.promptAfterClient }}
      </p>

      <section class="mb-7 rounded-lg border border-white/5 bg-sw-dark-2 p-5" aria-labelledby="oauth-warning">
        <p id="oauth-warning" class="m-0 text-base">
          {{ payload.warning }}
          <code class="mt-3 block whitespace-normal text-sm text-sw-purple break-all">{{ payload.redirectUri }}</code>
        </p>
      </section>

      <section v-if="payload.scopes.length" class="mb-7" aria-labelledby="oauth-permissions-title">
        <p id="oauth-permissions-title" class="m-0 mb-3.5 text-base">{{ payload.permissionsLabel }}</p>
        <ul class="m-0 list-none p-0">
          <li v-for="scope in payload.scopes" :key="scope" class="flex min-w-0 items-start gap-4">
            <span class="shrink-0 text-2xl leading-tight font-semibold text-sw-purple" aria-hidden="true">✓</span>
            <span class="min-w-0 text-sw-purple wrap-break-word">{{ scope }}</span>
          </li>
        </ul>
      </section>

      <div class="grid gap-3.5">
        <Button form="oauth-authorize-form" type="submit" variant="primary" size="lg">
          {{ payload.authorizeLabel }}
        </Button>
        <Button form="oauth-deny-form" type="submit" variant="outline" size="lg">
          {{ payload.denyLabel }}
        </Button>
      </div>
    </main>

    <main v-else class="min-w-0 leading-6 text-white">
      <p class="m-0 whitespace-pre-wrap rounded-lg border border-sw-purple/50 bg-sw-dark-2 p-5 break-words">
        {{ payload.error }}
      </p>
    </main>
  </AuthLayout>
</template>
